import {
  FiiDiiLatestSnapshot,
  FiiDiiHistoryPayload,
  FiiDiiMonthlyPayload,
} from '../types';
import { API_CONFIG } from '../constants';

const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes cache

let latestCache: { data: FiiDiiLatestSnapshot; timestamp: number } | null = null;
let historyCache: { data: FiiDiiHistoryPayload; timestamp: number } | null = null;
let monthlyCache: { data: FiiDiiMonthlyPayload; timestamp: number } | null = null;

let pendingLatestFetch: Promise<FiiDiiLatestSnapshot> | null = null;
let pendingHistoryFetch: Promise<FiiDiiHistoryPayload> | null = null;
let pendingMonthlyFetch: Promise<FiiDiiMonthlyPayload> | null = null;

/**
 * Formats a number in ₹ Crores with Indian localized number formatting and optional sign.
 */
export const formatCr = (value: number | undefined | null, includeSign = false): string => {
  if (value === undefined || value === null || isNaN(value)) return '₹0 Cr';
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  const sign = value > 0 ? (includeSign ? '+' : '') : value < 0 ? '-' : '';
  return `${sign}₹${formatted} Cr`;
};

export const FALLBACK_LATEST: FiiDiiLatestSnapshot = {
  date: '2026-10-06',
  displayDate: '06 Oct 2026',
  updatedAt: new Date().toISOString(),
  fii: { buy: 11000, sell: 13961.3, net: -2961.3 },
  dii: { buy: 18000, sell: 12911.08, net: 5088.92 },
  combinedNet: 2127.62,
  kpis: {
    todayNet: 2127.62,
    fiiTodayNet: -2961.3,
    diiTodayNet: 5088.92,
    d5Net: { fii: -37273.29, dii: 38536.82, combined: 1263.53 },
    mtdNet: { fii: -17144.66, dii: 20312.38, combined: 3167.72 },
    ytdNet: { fii: -61157.48, dii: 96342.67, combined: 35185.19 },
  },
};

export const FALLBACK_HISTORY: FiiDiiHistoryPayload = {
  generatedAt: new Date().toISOString(),
  totalDays: 5,
  records: [
    {
      date: '2026-10-06',
      fiiBuy: 11000,
      fiiSell: 13961.3,
      fiiNet: -2961.3,
      diiBuy: 18000,
      diiSell: 12911.08,
      diiNet: 5088.92,
      combinedNet: 2127.62,
    },
    {
      date: '2026-10-05',
      fiiBuy: 10500,
      fiiSell: 12000,
      fiiNet: -1500,
      diiBuy: 16000,
      diiSell: 14000,
      diiNet: 2000,
      combinedNet: 500,
    },
    {
      date: '2026-10-04',
      fiiBuy: 12100,
      fiiSell: 14500,
      fiiNet: -2400,
      diiBuy: 17200,
      diiSell: 13900,
      diiNet: 3300,
      combinedNet: 900,
    },
    {
      date: '2026-10-01',
      fiiBuy: 9500,
      fiiSell: 11000,
      fiiNet: -1500,
      diiBuy: 14800,
      diiSell: 12900,
      diiNet: 1900,
      combinedNet: 400,
    },
    {
      date: '2026-09-30',
      fiiBuy: 9000,
      fiiSell: 9500,
      fiiNet: -500,
      diiBuy: 11000,
      diiSell: 9000,
      diiNet: 2000,
      combinedNet: 1500,
    },
  ],
};

export const FALLBACK_MONTHLY: FiiDiiMonthlyPayload = {
  generatedAt: new Date().toISOString(),
  range: '2012-2026',
  totalMonths: 10,
  monthlyData: [
    { year: 2026, month: 10, monthName: 'Oct', monthKey: '2026-10', fiiNet: -17145, diiNet: 20312, combinedNet: 3167 },
    { year: 2026, month: 9, monthName: 'Sep', monthKey: '2026-09', fiiNet: -44013, diiNet: 38000, combinedNet: -6013 },
    { year: 2026, month: 8, monthName: 'Aug', monthKey: '2026-08', fiiNet: 7320, diiNet: 48200, combinedNet: 55520 },
    { year: 2025, month: 12, monthName: 'Dec', monthKey: '2025-12', fiiNet: -9668, diiNet: 12000, combinedNet: 2332 },
  ],
  yearlyTotals: [
    { year: 2026, fiiNet: -61157, diiNet: 96343, combinedNet: 35186, months: 10 },
    { year: 2025, fiiNet: -253823, diiNet: 280000, combinedNet: 26177, months: 12 },
  ],
};

/**
 * Fetches the latest session snapshot (used by the Header Ticker and Explorer modal).
 */
export async function fetchFiiDiiLatest(forceRefresh = false): Promise<FiiDiiLatestSnapshot> {
  const now = Date.now();
  if (!forceRefresh && latestCache && now - latestCache.timestamp < CACHE_TTL_MS) {
    return latestCache.data;
  }

  if (pendingLatestFetch && !forceRefresh) {
    return pendingLatestFetch;
  }

  pendingLatestFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FII_DII_LATEST}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch latest FII/DII data: ${response.status}`);
      }
      const data: FiiDiiLatestSnapshot = await response.json();
      latestCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      latestCache = { data: FALLBACK_LATEST, timestamp: Date.now() };
      return FALLBACK_LATEST;
    } finally {
      pendingLatestFetch = null;
    }
  })();

  return pendingLatestFetch;
}

/**
 * Fetches daily time-series records for charts and historical table.
 */
export async function fetchFiiDiiHistory(forceRefresh = false): Promise<FiiDiiHistoryPayload> {
  const now = Date.now();
  if (!forceRefresh && historyCache && now - historyCache.timestamp < CACHE_TTL_MS) {
    return historyCache.data;
  }

  if (pendingHistoryFetch && !forceRefresh) {
    return pendingHistoryFetch;
  }

  pendingHistoryFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FII_DII_HISTORY}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch FII/DII history: ${response.status}`);
      }
      const data: FiiDiiHistoryPayload = await response.json();
      historyCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      historyCache = { data: FALLBACK_HISTORY, timestamp: Date.now() };
      return FALLBACK_HISTORY;
    } finally {
      pendingHistoryFetch = null;
    }
  })();

  return pendingHistoryFetch;
}

/**
 * Fetches 14-year monthly matrix and yearly aggregate dataset (2012-2026).
 */
export async function fetchFiiDiiMonthly(forceRefresh = false): Promise<FiiDiiMonthlyPayload> {
  const now = Date.now();
  if (!forceRefresh && monthlyCache && now - monthlyCache.timestamp < CACHE_TTL_MS) {
    return monthlyCache.data;
  }

  if (pendingMonthlyFetch && !forceRefresh) {
    return pendingMonthlyFetch;
  }

  pendingMonthlyFetch = (async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FII_DII_MONTHLY}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch FII/DII monthly data: ${response.status}`);
      }
      const data: FiiDiiMonthlyPayload = await response.json();
      monthlyCache = { data, timestamp: Date.now() };
      return data;
    } catch {
      monthlyCache = { data: FALLBACK_MONTHLY, timestamp: Date.now() };
      return FALLBACK_MONTHLY;
    } finally {
      pendingMonthlyFetch = null;
    }
  })();

  return pendingMonthlyFetch;
}
